import { ChunkLoadErrorPage } from "Components/Pages/ChunkLoadErrorPage";
import { NotFoundPage } from "Components/Pages/NotFoundPage";
import { UnauthenticatedPage } from "Components/Pages/UnauthenticatedPage";
import { LogCategory } from "Enumerations";
import React, { useEffect, useMemo } from "react";
import { isRouteErrorResponse, useInRouterContext, useRouteError } from "react-router";
import { useLogging } from "Shared/Hooks";
import { newGuid } from "Shared/Utils";

import { getChunkLoadFailure } from "./ChunkLoadRecovery";

enum ErrorType {
    General,
    ChunkLoadError,
    NotFound,
    Route
}

const getErrorType = (error: unknown) => {
    if (isRouteErrorResponse(error)) {
        return error.status === 404 ? ErrorType.NotFound : ErrorType.Route;
    }
    return getChunkLoadFailure(error) ? ErrorType.ChunkLoadError : ErrorType.General;
};

const tagId = "1e6f11d2-66dc-4384-8741-f3fba5c800c6";
const correlationId = newGuid();

export const ErrorBoundary = ({ errorProp }: { errorProp?: unknown }) => {
    const inRouter = useInRouterContext();
    return inRouter ? <RouterErrorBoundary errorProp={errorProp} /> : <ErrorBoundaryInner error={errorProp} />;
};

const RouterErrorBoundary = ({ errorProp }: { errorProp?: unknown }) => {
    const error = useRouteError() ?? errorProp;
    return <ErrorBoundaryInner error={error} />;
};

const ErrorBoundaryInner = ({ error }: { error: unknown }) => {
    const { trackException } = useLogging(LogCategory.Core);
    const errorType = useMemo(() => getErrorType(error), [error]);

    const errorMessage = error instanceof Error ? error.stack : JSON.stringify(error);

    useEffect(() => {
        trackException(tagId, error, "uncaught exception", { CorrelationId: correlationId });
    }, [error, trackException]);

    switch (errorType) {
        case ErrorType.NotFound:
            return <NotFoundPage />;
        case ErrorType.ChunkLoadError:
            return <ChunkLoadErrorPage error={error} correlationId={correlationId} />;
        case ErrorType.Route:
            return (
                <UnauthenticatedPage
                    content={
                        <>
                            <p>
                                There is a routing error. If you have questions please contact OSP team (osponcall@microsoft.com) with the following information
                                (Please copy & paste)
                            </p>
                            <p>Tag ID: {tagId}</p>
                            <p>Correlation ID: {correlationId}</p>
                            <p>Url: {window.location.href}</p>
                            <p>Error: {errorMessage}</p>
                        </>
                    }
                />
            );
        default:
            return (
                <UnauthenticatedPage
                    content={
                        <>
                            <p>
                                Something went wrong, the crash logs were collected and we will be looking into them, but in the meantime please try refreshing
                                the page with Ctrl+F5 to see if it works. If you have other requirement please contact OSP team (osponcall@microsoft.com) with
                                the following information (Please copy & paste):
                            </p>
                            <p>Tag ID: {tagId}</p>
                            <p>Correlation ID: {correlationId}</p>
                            <p>Url: {window.location.href}</p>
                            <p>Error: {errorMessage}</p>
                        </>
                    }
                />
            );
    }
};
